import React from "react";
import SiteHeader from "../components/SiteHeader.jsx";
import SiteFooter from "../components/SiteFooter.jsx";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <SiteHeader />
      <main className="max-w-3xl mx-auto px-6 py-14 flex-1" data-testid="privacy-policy-page">
        <div className="text-xs tracking-widest uppercase text-[#111827] font-semibold">Legal</div>
        <h1 className="font-heading text-4xl font-extrabold text-[#111827] mt-2">Privacy Policy — RapidCare SA</h1>
        <p className="text-sm text-slate-500 mt-2">Last updated: April 2026</p>

        <div className="prose prose-slate max-w-none mt-8 text-slate-700 leading-relaxed space-y-6">
          <section>
            <h2 className="font-heading text-xl font-bold text-[#111827]">1. Introduction</h2>
            <p>RapidCare SA ("we", "us", "our") is committed to protecting your personal information in accordance with the Protection of Personal Information Act, 2013 (POPIA) of South Africa. This Privacy Policy explains how we collect, use, store, and protect your data.</p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-[#111827]">2. Information We Collect</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Account Information:</strong> Email address, name, and hashed password when you register.</li>
              <li><strong>Subscription Data:</strong> Payment transaction records processed securely through Payfast. We do not store credit card numbers.</li>
              <li><strong>Usage Data:</strong> Pages visited, protocols viewed, and features accessed to improve the platform.</li>
              <li><strong>Reference History:</strong> We keep a log of which protocols, drugs, calculators, and ICD-10 codes you view so that we can show your recent items and favorites. This does not include any patient-identifiable information.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-[#111827]">3. Information We Do Not Collect</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Patient Data:</strong> RapidCare SA does not store identifiable patient data. Users should not enter patient-identifiable information into any feature of the platform.</li>
              <li><strong>Biometric Data:</strong> Biometric authentication (fingerprint/face) is processed locally on your device and never transmitted to our servers.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-[#111827]">4. How We Use Your Information</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>To provide and maintain the RapidCare SA service.</li>
              <li>To process subscriptions and payments via Payfast.</li>
              <li>To improve platform content, features, and user experience.</li>
              <li>To send essential service notifications (e.g., subscription status, guideline updates).</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-[#111827]">5. Data Storage and Security</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>Data is stored on secure, encrypted servers.</li>
              <li>Passwords are hashed using bcrypt and never stored in plain text.</li>
              <li>Payment processing is handled by Payfast, a PCI-DSS compliant payment provider.</li>
              <li>Access to personal data is restricted to authorised personnel only.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-[#111827]">6. Third-Party Services</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Payfast:</strong> Processes payments. Subject to Payfast's own privacy policy.</li>
              <li><strong>Emergent (Google OAuth):</strong> If you sign in with Google, the Emergent OAuth proxy verifies your Google identity. We receive only your verified email, name, and avatar.</li>
              <li><strong>Google OAuth:</strong> Optional sign-in method. Subject to Google's privacy policy.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-[#111827]">7. Your Rights Under POPIA</h2>
            <p>You have the right to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Access your personal information held by us.</li>
              <li>Request correction of inaccurate personal information.</li>
              <li>Request deletion of your personal information.</li>
              <li>Object to the processing of your personal information.</li>
              <li>Lodge a complaint with the Information Regulator of South Africa.</li>
            </ul>
            <p className="mt-2">To exercise any of these rights, contact us at <a href="mailto:support@rapidcare.co.za" className="text-[#111827] underline">support@rapidcare.co.za</a>.</p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-[#111827]">8. Data Retention</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>Account data is retained for the duration of your account.</li>
              <li>Upon account deletion, personal data is removed within 30 days.</li>
              <li>Anonymous, aggregated usage data may be retained for platform improvement.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-[#111827]">9. Cookies, Local Storage &amp; Analytics</h2>
            <p>RapidCare SA uses local storage (not cookies) to save your preferences, authentication tokens, and theme settings. We also use third-party analytics and advertising-measurement tools, as described below and in our <a href="/cookie-policy" className="text-[#111827] underline">Cookie Policy</a>.</p>
            <p className="mt-3">
              We use <strong>Google Analytics 4</strong> and <strong>Google Ads</strong> conversion tracking, loaded via <strong>Google Tag Manager</strong>, to understand how the platform is used and to measure the effectiveness of our advertising (for example, which advert led to a sign-up). These tools may set Google cookies and process aggregate usage and device information. We never send clinical or patient data to Google. Google processes this data under its own <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[#111827] underline">privacy policy</a>; you can opt out of Google Analytics with the <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-[#111827] underline">Google Analytics opt-out add-on</a>.
            </p>
            <p className="mt-3">
              We also use <strong>PostHog</strong>, a product-analytics and session-recording service, to understand how the platform is used and to improve it. This may include <strong>session replay</strong> (a reconstruction of page interactions such as clicks and navigation). We configure PostHog to <strong>mask text inputs</strong> so that sensitive information you type — including email addresses and any free-text you enter into forms such as the feedback form — is <strong>not captured</strong> in recordings. We never intentionally record patient-identifiable information, and you should never enter such information into the platform. PostHog processes this data as our processor; see the <a href="https://posthog.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[#111827] underline">PostHog privacy policy</a> for details.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-[#111827]">10. Children</h2>
            <p>RapidCare SA is intended for qualified healthcare professionals and is not directed at individuals under the age of 18.</p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-[#111827]">11. Changes to This Policy</h2>
            <p>We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated revision date.</p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-[#111827]">12. Contact</h2>
            <p>For privacy-related queries:</p>
            <p>
              Email: <a href="mailto:rapidcaresasupport@gmail.com" className="text-[#111827] underline">rapidcaresasupport@gmail.com</a>
              <br />
              Website: rapidcare.co.za
            </p>
            <p className="mt-3">
              Information Regulator (South Africa):<br />
              Website: <a href="https://inforegulator.org.za" target="_blank" rel="noopener noreferrer" className="text-[#111827] underline">https://inforegulator.org.za</a>
            </p>
          </section>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
